import * as Sentry from "@sentry/react";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { debounce, isEqual } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { default as InlineSVG, default as SVG } from "react-inlinesvg";
import { cn } from "src/lib/cn.js";
import { useImmer } from "use-immer";
import { useAutoSave } from "../../hooks/useAutoSave";
import { AI_VISIBILITY } from "../../../../src/shared/enums.js";
import { BLANK_TIPTAP_JSON, CharacterSchema, CustomFieldSchema, DEFAULT_CHARACTER_CUSTOM_FIELDS, DEFAULT_ROLE_OPTIONS, MAX_NUM_CUSTOM_FIELDS, extractTextFromTipTap } from "../../../../src/shared/schemas/CharacterSchema.js";
import {
  buildCharacterSavePayload,
  characterHasOversizedFields,
  getCharacterOversizedFieldKeys,
  mergeCharacterPreservingOversizedFields,
  storyBibleFieldsNotSavedToastMessage,
} from "../../../../src/shared/schemas/storyBibleFieldLimit.js";
import { formatCharacterRoleLabel, normalizeCharacterRole } from "../../../../src/shared/characterTemplates.js";
import { AppEventPublisher, StoryBibleAction, StoryBibleType } from "../../AppEventPublisher.js";
import Auth from "../../Auth.js";
import useOnClickOutside from "../../hooks/useOnClickOutside.js";
import Project from "../../models/Project.js";
import { useAppStore } from "../../stores/appStore.js";
import { useProjectStore } from "../../stores/projectStore.js";
import { useStoryBibleStore } from "../../stores/storyBibleStore.js";
import { SudoButton } from "../../sudokit/components/SudoButton/index.jsx";
import { themeVariables } from "../../themeVariables.js";
import { EXPAND_STORY_BIBLE_ENTITY_EVENT, getStoryBibleEntityExpandRequestId } from "../PersistentChat/mentions/navigateToEntityMention";
import AnimatedWrapper from "../AnimatedWrapper/index.js";
import CustomField from "../CustomField/index.js";
import useElementHistoryController from "../ElementHistory/useElementHistoryController";
import ElementVisibilityController from "../ElementVisibilityController";
import HistoryModal from "../HistoryModal/HistoryModal";
import HistorySelectedItem from "../HistoryModal/HistorySelectedItem";
import IconButton from "../IconButton/index.jsx";
import CharacterPortraitDialog from "./CharacterImage/CharacterPortraitDialog.tsx";
import EntityImageGalleryPreview, { MAX_VISIBLE_ALTERNATES } from "../EntityImage/EntityImageGalleryPreview.tsx";
import EntityImageRowThumbnail from "../EntityImage/EntityImageRowThumbnail.tsx";
import { usePortraitGeneration } from "./CharacterImage/lib/usePortraitGeneration";
import { useEntityImageCard, useEntityImageDialogState } from "../EntityImage/lib/useEntityImageCard";
import { useCharacterPortraits } from "./CharacterImage/lib/useCharacterPortraits";
import { deleteCharacterPortrait, listCharacterPortraits, setCoverCharacterPortrait } from "./CharacterImage/lib/api";
import SeriesTag from "../StoryBible/SeriesTag.tsx";
import { characterToPlainText } from "../StoryBible/Utils";
import StyledSelect from "../StyledSelect";
import { ReactSelectFocusManagerContext } from "../StyledSelect/ReactSelectFocusManager";
import { RestorableTiptapField } from "../TiptapField/index.js";
import { useToast } from "../ToastWrapper/index.js";
import { hasUnsavedEditsSince, reconcileEntityContent, PARENT_SYNCED_METADATA_FIELDS } from "../StoryBible/entitySync";
import { Container, DotsDeleteMenuContainer } from "./styles.js";
import { highlightEntityField } from "src/utils/diffToTiptap";
import { useSudowriteStore } from "../../stores/sudowriteStore.js";
import { useSudoLayoutEventEmitter } from "src/lib/layout-events";
import { usePersistentChatStore } from "src/stores/persistentChatStore";
import { useCrossAppSignalStore, getTraitCTASignal } from "src/stores/crossAppSignalStore";
import { mergeCustomFieldsWithTemplateOrder } from "src/utils/mergeCustomFieldsWithTemplateOrder.js";

// these are all tiptap fields
const SMALL_FIELDS = ["otherNames", "groups", "pronouns"];

const BLANK_TIPTAP_JSON_STRING = JSON.stringify(BLANK_TIPTAP_JSON);

const isFieldBlank = (field) => {
  if (!field) return true;
  if (field.value === undefined || field.value === null) return true;
  return JSON.stringify(field.value) === BLANK_TIPTAP_JSON_STRING;
};

// when the width of the center column is less than this, we switch to column layout for the header
const SMALL_WIDTH_BREAKPOINT = 600;
const COLLAPSE_ICONS_BREAKPOINT = 600;
const COLLAPSE_ICONS_WHEN_SERIES_BREAKPOINT = 780; // break sooner if series is present
const MOVE_ROLE_SELECTOR_BREAKPOINT = 700;

const convertKeyToLabel = (key) => {
  return key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());
};

// Mirrors the custom-field half of the server's physical-description resolution
// (normalizeCharacterCustomFieldLabel + firstMatchingCharacterCustomField in
// src/models/Character.js) — only the custom-field lookup applies client-side, since the legacy
// physicalDescription/physicalDescriptionJSON columns never reach the client.
const normalizeCustomFieldLabel = (label) => (label ?? "").toString().trim().toLowerCase().replace(/\s+/g, " ");

const resolvePhysicalDescriptionCustomFieldText = (customFields) => {
  if (!Array.isArray(customFields)) return null;
  for (const field of customFields) {
    if (field.aiVisibility === AI_VISIBILITY.HIDDEN) continue;
    if (normalizeCustomFieldLabel(field.label) !== "physical description") continue;
    const text = extractTextFromTipTap(field.value).trim();
    if (text) return text;
  }
  return null;
};

export default function Character({
  //
  initialCharacter,
  projectId,
  dragListeners,
  isDragging,
  updateCharacterData,
  setActivatorNodeRef,
  readOnly,
  draggingLock,
  isSelected,
  handleSelect,
  onDeleteMultiple,
  isDeleting,
  selectedItems,
  syncing,
  getTemplateForRole,
  roleOptions = DEFAULT_ROLE_OPTIONS,
}) {
  const userId = useAppStore((state) => state.user?.uid);
  const [character, setCharacter] = useImmer(initialCharacter);
  // Latest committed character, so async save callbacks can detect edits made
  // while their request was in flight.
  const characterRef = useRef(character);
  useEffect(() => {
    characterRef.current = character;
  }, [character]);
  // Last server-persisted snapshot — never the local merge that re-includes oversized text.
  const persistedCharacterRef = useRef(initialCharacter);
  const previousParentRef = useRef(initialCharacter);
  // Text responses and their parent echoes can retain any pre-mutation portrait.
  // Keep this provenance separate from parent publications of recovered local text.
  const supersededPortraitsRef = useRef([]);
  const portraitSyncGenerationRef = useRef(0);
  const portraitReadPendingRef = useRef(false);
  const scheduledEntityRef = useRef(character);
  const reconcileQueueRef = useRef(() => {});

  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState({});
  const collapsed = character.collapsed;
  const [smallWidth, setSmallWidth] = useState(false);
  const [collapseIcons, setCollapseIcons] = useState(false);
  const columnWidths = useProjectStore((state) => state.columnWidths);
  const { setToast } = useToast();
  const [localDirty, setLocalDirty] = useState(false);
  const characterDiffs = useStoryBibleStore((state) => state.characterDiffs);
  const clearCharacterDiff = useStoryBibleStore((state) => state.clearCharacterDiff);
  const clearCharacterFieldDiff = useStoryBibleStore((state) => state.clearCharacterFieldDiff);
  const pendingNewCharacterIds = useStoryBibleStore((state) => state.pendingNewCharacterIds) || [];
  const removePendingNewCharacter = useStoryBibleStore((state) => state.removePendingNewCharacter);
  const [showHoverIcons, setShowHoverIcons] = useState(false);
  const [moveRoleSelector, setMoveRoleSelector] = useState(false);
  const isTogglingCollapsed = useRef(false);
  const targetCollapsedState = useRef(null);
  const lastVisibilitySetting = useRef(initialCharacter.aiVisibility);

  // this is used to unset z-index across many fields when it's appropriate.
  // this helps manipulate the various stacking contexts to prevent
  // overlapping in the UX.
  const [isFlat, setIsFlat] = useState(false);
  const portraitDialog = useEntityImageDialogState();
  const portraits = useCharacterPortraits(character.id, { enabled: !collapsed || portraitDialog.isOpen });
  const seed = useStoryBibleStore((state) => state.seed);
  const synopsis = useStoryBibleStore((state) => state.synopsis);
  const seedRef = useRef(seed);
  const synopsisRef = useRef(synopsis);
  const refreshCharacters = useStoryBibleStore((state) => state.refreshCharacters);
  const upsertCharacterEntity = useStoryBibleStore((state) => state.upsertCharacterEntity);
  const [projectTitle, setProjectTitle] = useState("");
  const { focusedElement, focusElement } = useContext(ReactSelectFocusManagerContext);
  const roleSelectRefs = useRef({});
  const setRoleSelectRef = useCallback((key, node) => {
    if (!key) return;
    if (node) {
      roleSelectRefs.current[key] = node;
    } else {
      delete roleSelectRefs.current[key];
    }
  }, []);
  const characterDiff = readOnly ? null : characterDiffs[character.id];
  const isPendingNew = readOnly ? false : pendingNewCharacterIds.includes(character.id);
  const hasDiff = isPendingNew || (characterDiff && characterDiff.oldFields && characterDiff.newFields);

  const hasAccessToAgenticChat = useSudowriteStore((state) => state.subscriptionJSON?.hasAccessToAgenticChat);
  const emitLayoutEvent = useSudoLayoutEventEmitter();
  const globalCtaActive = usePersistentChatStore((state) => {
    const cta = state.ctaAction;
    return cta && cta.status === "in_progress";
  });
  const traitSignals = useCrossAppSignalStore((state) => state.signals);

  useEffect(() => {
    seedRef.current = seed;
  }, [seed]);

  useEffect(() => {
    synopsisRef.current = synopsis;
  }, [synopsis]);

  const getFieldDiffValue = useCallback((fieldKey, currentValue) => (
    highlightEntityField(currentValue, fieldKey, characterDiff, isPendingNew)
  ), [isPendingNew, characterDiff, character.id]);

  const clearAllCharacterHighlighting = useCallback(() => {
    clearCharacterDiff(character.id);
    removePendingNewCharacter(character.id);
  }, [character.id, clearCharacterDiff, removePendingNewCharacter]);

  const clearFieldHighlighting = useCallback((fieldKey) => {
    // For NEW entities, clicking any field clears all highlights
    if (isPendingNew) {
      clearCharacterDiff(character.id);
      removePendingNewCharacter(character.id);
      return;
    }
    // For MODIFIED entities, only clear that specific field
    if (characterDiff) {
      clearCharacterFieldDiff(character.id, fieldKey);
    }
  }, [character.id, isPendingNew, characterDiff, clearCharacterDiff, clearCharacterFieldDiff, removePendingNewCharacter]);

  const customRoleOption = useMemo(() => ({ value: "sudo-custom", label: "Custom", source: "customTrigger" }), []);
  const roleOptionsList = useMemo(() => {
    const options = roleOptions?.length ? roleOptions : DEFAULT_ROLE_OPTIONS;
    const filtered = options.filter((option) => option.value !== customRoleOption.value);
    const merged = [customRoleOption, ...filtered];
    return merged.map((option) => ({
      ...option,
      source: option.source || (option.value === customRoleOption.value ? "customTrigger" : "template"),
    }));
  }, [customRoleOption, roleOptions]);
  const simplifiedRoleSelectRefKey = `character-role-simplified-${character.id}`;

  const primaryCharacterRole = useMemo(() => normalizeCharacterRole(character.roles), [character.roles]);
  const characterTraitTemplateOrderKey = useMemo(() => {
    const tmpl = getTemplateForRole?.(primaryCharacterRole);
    if (!tmpl?.defaultFields?.length || tmpl.role !== primaryCharacterRole) return "";
    return tmpl.defaultFields.map((f) => f.label).join("\u0001");
  }, [getTemplateForRole, primaryCharacterRole]);
  const cardTraitLabelsKey = useMemo(
    () => (character.customFields?.length ? character.customFields.map((f) => f.label).join("\u0001") : ""),
    [character.customFields],
  );
  const characterNameText = useMemo(() => extractTextFromTipTap(character.name), [character.name]);
  const characterDisplayName = characterNameText || "Character";
  const oversizedFieldKeys = useMemo(() => getCharacterOversizedFieldKeys(character), [character]);
  const physicalDescriptionText = useMemo(
    () => resolvePhysicalDescriptionCustomFieldText(character.customFields),
    [character.customFields],
  );
  const pronounsText = useMemo(() => extractTextFromTipTap(character.pronouns), [character.pronouns]);

  const applySavedPortrait = useCallback((imageId, imageUrl) => {
    portraitSyncGenerationRef.current += 1;
    portraitReadPendingRef.current = false;
    const latest = characterRef.current;
    const before = { imageId: latest.imageId, imageUrl: latest.imageUrl };
    const after = { imageId, imageUrl };
    if (!isEqual(before, after)) {
      supersededPortraitsRef.current = supersededPortraitsRef.current.filter((pair) => !isEqual(pair, after));
      if (!supersededPortraitsRef.current.some((pair) => isEqual(pair, before))) supersededPortraitsRef.current.push(before);
    }
    const updated = { ...latest, ...after };
    characterRef.current = updated;
    // Portrait endpoints already persisted this change. Keep any queued text work,
    // but do not schedule another full-content PUT just for the portrait fields.
    if (isEqual(scheduledEntityRef.current, latest)) scheduledEntityRef.current = updated;
    setCharacter(updated);
    if (useProjectStore.getState().project?.id === projectId) {
      updateCharacterData(updated);
      upsertCharacterEntity(updated, projectId);
    }
  }, [projectId, setCharacter, updateCharacterData, upsertCharacterEntity]);

  const acceptSavedEntity = useCallback((server, submitted, { fromParent = false, portrait = characterRef.current } = {}) => {
    if (!server || server.id !== submitted.id || !server.name || !Array.isArray(server.customFields) || !server.updatedAt || !Number.isFinite(new Date(server.updatedAt).getTime())) {
      reconcileQueueRef.current(persistedCharacterRef.current.updatedAt);
      return;
    }
    if (new Date(server.updatedAt).getTime() < new Date(persistedCharacterRef.current.updatedAt).getTime()) return;
    const latest = characterRef.current;
    const content = reconcileEntityContent(submitted, latest, server);
    const oversizedKeys = getCharacterOversizedFieldKeys(latest);
    const localFields = new Map((latest.customFields || []).map((field) => [field.id, field]));
    const retainedFields = content.customFields.map((field) => oversizedKeys.has(`customField:${field.id}`)
      ? { ...field, value: localFields.get(field.id).value }
      : field);
    const recovered = mergeCharacterPreservingOversizedFields(content, { ...latest, customFields: retainedFields });
    // Visibility is metadata, but a toggle made after submission is still an intentional save.
    if (latest.aiVisibility !== submitted.aiVisibility) recovered.aiVisibility = latest.aiVisibility;
    if (latest.collapsed !== submitted.collapsed) recovered.collapsed = latest.collapsed;
    // Text saves do not write portraits, so their responses can contain stale image fields.
    // Parent sync independently decides whether its portrait pair is authoritative.
    recovered.imageId = portrait.imageId;
    recovered.imageUrl = portrait.imageUrl;
    const comparableServer = { ...server, imageId: recovered.imageId, imageUrl: recovered.imageUrl };
    persistedCharacterRef.current = server;
    const payload = buildCharacterSavePayload(recovered, server).character;
    const needsSave = hasUnsavedEditsSince(payload, comparableServer) || payload.aiVisibility !== server.aiVisibility;
    characterRef.current = recovered;
    scheduledEntityRef.current = recovered;
    setCharacter(recovered);
    setLocalDirty(needsSave || characterHasOversizedFields(recovered));
    // Register replacement work before releasing the old request's promise chain.
    reconcileQueueRef.current(server.updatedAt, needsSave ? recovered : undefined);
    if (!fromParent && useProjectStore.getState().project?.id === projectId) {
      previousParentRef.current = recovered;
      updateCharacterData(recovered);
      upsertCharacterEntity(recovered, projectId);
    }
  }, [projectId, setCharacter, updateCharacterData, upsertCharacterEntity]);

  useEffect(() => {
    // If we're toggling and waiting for a specific collapsed state,
    // only sync from parent if it matches what we expect
    if (isTogglingCollapsed.current && targetCollapsedState.current !== null) {
      if (initialCharacter.collapsed !== targetCollapsedState.current) {
        // Ignore this update, we're still waiting for the right state
        return;
      }
      // Got the state we were waiting for; fall through to the dirty-aware sync
      // below so text typed during the toggle round-trip isn't replaced.
      isTogglingCollapsed.current = false;
      targetCollapsedState.current = null;
    }

    const shouldPreserveLocalVisibility = lastVisibilitySetting.current && initialCharacter.aiVisibility !== lastVisibilitySetting.current;

    if (new Date(initialCharacter.updatedAt).getTime() < new Date(persistedCharacterRef.current.updatedAt).getTime()) return;
    const latest = characterRef.current;
    const parentPortrait = { imageId: initialCharacter.imageId, imageUrl: initialCharacter.imageUrl };
    const localPortrait = { imageId: latest.imageId, imageUrl: latest.imageUrl };
    const ambiguousPortrait = supersededPortraitsRef.current.some((pair) => isEqual(pair, parentPortrait));
    const portrait = ambiguousPortrait ? localPortrait : parentPortrait;
    if (!ambiguousPortrait && !isEqual(parentPortrait, localPortrait)) {
      // A distinct external change establishes a new portrait baseline.
      supersededPortraitsRef.current = [];
      portraitReadPendingRef.current = false;
    }
    const parentCharacter = ambiguousPortrait ? { ...initialCharacter, ...portrait } : initialCharacter;
    const previousParent = previousParentRef.current;
    previousParentRef.current = initialCharacter;
    if (initialCharacter.updatedAt !== previousParent.updatedAt) {
      // Bulk metadata responses only change metadata in the parent. They do not
      // provide a new content snapshot and must not replace the persisted content.
      const authority = hasUnsavedEditsSince({ ...initialCharacter, ...portrait }, { ...previousParent, ...portrait })
        ? initialCharacter
        : { ...persistedCharacterRef.current, ...Object.fromEntries(PARENT_SYNCED_METADATA_FIELDS.map((key) => [key, initialCharacter[key]])) };
      acceptSavedEntity(authority, persistedCharacterRef.current, { fromParent: true, portrait });
      return;
    }

    if (localDirty) {
      // Only sync metadata fields (PARENT_SYNCED_METADATA_FIELDS in StoryBible/entitySync.ts),
      // preserve content edits in progress
      setCharacter((draft) => {
        draft.imageId = portrait.imageId;
        draft.imageUrl = portrait.imageUrl;
        draft.updatedAt = initialCharacter.updatedAt;
        draft.collapsed = initialCharacter.collapsed;
        draft.displayOrder = initialCharacter.displayOrder;
        if (!shouldPreserveLocalVisibility) {
          draft.aiVisibility = initialCharacter.aiVisibility;
        }
      });
    } else if (shouldPreserveLocalVisibility) {
      // this stops the flickering back to old visibility when the user changes the
      // visibility and before changes are saved
      setCharacter((draft) => {
        Object.assign(draft, parentCharacter);
        draft.aiVisibility = lastVisibilitySetting.current;
      });
    } else {
      setCharacter(parentCharacter);
    }
  }, [initialCharacter, setCharacter, localDirty, acceptSavedEntity]);

  useEffect(() => () => { portraitSyncGenerationRef.current += 1; }, []);

  useEffect(() => {
    if (new Date(initialCharacter.updatedAt).getTime() < new Date(persistedCharacterRef.current.updatedAt).getTime()) return;
    if (isTogglingCollapsed.current && initialCharacter.collapsed !== targetCollapsedState.current) return;
    // Only accepted parent transitions supersede a pending authority read.
    // An ignored older/collapse-mismatching publication must not discard it.
    const generation = ++portraitSyncGenerationRef.current;
    const parentPortrait = { imageId: initialCharacter.imageId, imageUrl: initialCharacter.imageUrl };
    const needsVerification = portraitReadPendingRef.current || supersededPortraitsRef.current.some((pair) => isEqual(pair, parentPortrait));
    if (!needsVerification) return;
    // Text-save publications carry the retained local pair, not fresh portrait
    // authority. Restart unresolved verification rather than silently dropping it.
    portraitReadPendingRef.current = true;

    // A previously replaced pair can be either a stale text echo or a genuine
    // remote restoration/deletion. Read the current cover directly, not the
    // gallery cache (which may predate the parent update).
    void listCharacterPortraits(initialCharacter.id, { limit: 1 }).then((result) => {
      if (generation !== portraitSyncGenerationRef.current) return;
      portraitReadPendingRef.current = false;
      if (!result?.success || (result.cover !== null && (typeof result.cover?.id !== "string" || typeof result.cover?.url !== "string"))) {
        throw new Error("Invalid character portrait list response");
      }
      const portrait = { imageId: result.cover?.id ?? null, imageUrl: result.cover?.url ?? null };
      const latest = characterRef.current;
      if (!isEqual(portrait, { imageId: latest.imageId, imageUrl: latest.imageUrl })) {
        applySavedPortrait(portrait.imageId, portrait.imageUrl);
      }
    }).catch((err) => {
      if (generation === portraitSyncGenerationRef.current) {
        portraitReadPendingRef.current = false;
        Sentry.captureException(err, { tags: { feature: "character_portrait_sync" } });
      }
    });
  }, [initialCharacter, applySavedPortrait]);

  // Run after parent sync so template order isn't overwritten by stale `initialCharacter` on the same tick.
  // Keep trait order aligned with Customize-defaults / template order when the template or on-card label order changes.
  // Do not drop blank non-template traits here: users add traits, name them, then fill the body; dropping
  // only runs on role change (handleRoleSelectChange) where stale template rows are cleared.
  useEffect(() => {
    if (readOnly || !characterTraitTemplateOrderKey) return;
    const tmpl = getTemplateForRole?.(primaryCharacterRole);
    const templateFields = tmpl?.defaultFields;
    if (!templateFields?.length || tmpl.role !== primaryCharacterRole) return;
    setCharacter((draft) => {
      const merged = mergeCustomFieldsWithTemplateOrder(
        draft.customFields,
        templateFields,
        (tf) => CustomFieldSchema.cast({ label: tf.label || "" }),
        { dropBlankExtrasNotInTemplate: false, insertMissingTemplateFields: false },
      );
      if (isEqual(merged, draft.customFields)) return;
      draft.customFields = merged;
    });
  }, [
    cardTraitLabelsKey,
    characterTraitTemplateOrderKey,
    getTemplateForRole,
    primaryCharacterRole,
    readOnly,
    setCharacter,
  ]);

  const revertUnsavedVisibility = useCallback((attemptedVisibility) => {
    const persistedVisibility = persistedCharacterRef.current.aiVisibility || AI_VISIBILITY.AUTO;
    if (attemptedVisibility === persistedVisibility || characterRef.current.aiVisibility !== attemptedVisibility) {
      return;
    }
    lastVisibilitySetting.current = persistedVisibility;
    setCharacter((draft) => {
      draft.aiVisibility = persistedVisibility;
    });
  }, [setCharacter]);

  // Save function for character
  const saveCharacter = useCallback(
    async (characterToSave) => {
      if (readOnly) return;
      if (!characterToSave) return;

      try {
        const { character: characterPayload, skippedFields } = buildCharacterSavePayload(
          characterToSave,
          persistedCharacterRef.current,
        );
        const validated = await CharacterSchema.validate({
          userId: characterPayload.userId,
          projectId: characterPayload.projectId,
          name: characterPayload.name,
          roles: characterPayload.roles,
          pronouns: characterPayload.pronouns,
          groups: characterPayload.groups,
          otherNames: characterPayload.otherNames,
          customFields: characterPayload.customFields,
          collapsed: characterPayload.collapsed,
          aiVisibility: characterPayload.aiVisibility,
        });

        const result = await Auth.fetchWithIdToken(`/api/characters/${characterToSave.id}`, {
          method: "PUT",
          character: {
            ...characterToSave,
            name: validated.name,
            roles: validated.roles,
            pronouns: validated.pronouns,
            groups: validated.groups,
            otherNames: validated.otherNames,
            customFields: validated.customFields,
            collapsed: validated.collapsed,
            aiVisibility: validated.aiVisibility,
          },
          updatedAt: characterToSave.updatedAt,
          projectId,
        });

        acceptSavedEntity(result.character, characterToSave);
        if (skippedFields.length > 0) {
          setToast(storyBibleFieldsNotSavedToastMessage());
        }
      } catch (err) {
        if (err?.name === "ValidationError") {
          setError(err);
          return;
        }
        if (err.status === 409) {
          try {
            const data = await err.response.json();
            if (data.character) acceptSavedEntity(data.character, characterToSave);
            else reconcileQueueRef.current(persistedCharacterRef.current.updatedAt);
          } catch (_) {
            // A malformed response cannot authorize a retry with a fresh timestamp.
            reconcileQueueRef.current(persistedCharacterRef.current.updatedAt);
          }
          return;
        }
        Sentry.captureException(err, { tags: { feature: "character_save" } });
        setToast("Could not save character");
        revertUnsavedVisibility(characterToSave.aiVisibility);
      }
    },
    [readOnly, projectId, acceptSavedEntity, setToast, revertUnsavedVisibility]
  );

  // Snapshot creation function
  const createCharacterSnapshot = useCallback(async () => {
    if (!character?.id || !localDirty) return; // Only create snapshot if there are changes

    try {
      await Auth.fetchWithIdToken(`/api/characters/${character.id}/snapshot`, {
        method: "POST",
        projectId,
      });
    } catch (err) {
      Sentry.captureException(err, { tags: { feature: "character_snapshot" } });
    }
  }, [character, projectId, localDirty]);

  // Use shared auto-save hook with blur/visibility handling and snapshot creation
  const { handleBlur: handleCharacterBlur, debouncedSave: updateCharacter, reconcile } = useAutoSave({
    saveFn: saveCharacter,
    entity: character,
    createSnapshot: createCharacterSnapshot,
    readOnly,
    version: initialCharacter?.updatedAt,
    versionPolicy: "explicit",
  });
  reconcileQueueRef.current = reconcile;

  const onFieldBlur = useCallback(async () => {
    if (!localDirty) return;
    await handleCharacterBlur();
  }, [handleCharacterBlur, localDirty]);

  const handleFieldChange = useCallback(
    (fieldKey, json) => {
      if (hasDiff) return;
      setCharacter((draft) => {
        draft[fieldKey] = json;
      });
    },
    [hasDiff, setCharacter],
  );

  const handleFieldFocus = useCallback(
    (fieldKey) => {
      if (hasDiff) {
        clearFieldHighlighting(fieldKey);
      }
    },
    [hasDiff, clearFieldHighlighting],
  );

  const handleNameChange = useCallback((json) => handleFieldChange("name", json), [handleFieldChange]);
  const handleNameFocus = useCallback(() => handleFieldFocus("name"), [handleFieldFocus]);

  const regenerateTrait = useCallback(
    async (traitId, traitLabel, guidance, isEmpty) => {
      if (readOnly) return;
      try {
        AppEventPublisher.storyBible.characters.generateTrait(isEmpty);
        const response = await Auth.fetchWithIdToken(`/api/ai/storyEngine/regenerateTrait`, {
          method: "POST",
          projectId,
          traitId,
          trait: traitLabel, // Keep for backward compatibility
          seed: seedRef.current,
          synopsis: synopsisRef.current,
          characterId: character.id,
          guidance,
        });
        updateCharacterData({ ...response.character, imageId: characterRef.current.imageId, imageUrl: characterRef.current.imageUrl });
        setCharacter((draft) => {
          Object.assign(draft, response.character);
          draft.imageId = characterRef.current.imageId;
          draft.imageUrl = characterRef.current.imageUrl;
        });
      } catch (e) {
        Sentry.captureException(e, { tags: { feature: "character_regenerate_trait" } });
        setToast("Failed to regenerate trait");
      }
    },
    [readOnly, projectId, character.id, updateCharacterData, setCharacter, setToast],
  );

  const handleTraitCtaTrigger = useCallback(
    (traitId, traitLabel, isEmpty) => {
      if (globalCtaActive) return;
      emitLayoutEvent("trigger_cta_chat", {
        type: isEmpty ? "generate_trait" : "rewrite_trait",
        targetId: character.id,
        targetName: characterDisplayName,
        displayTitle: isEmpty ? "Generate" : "Rewrite",
        userMessage: `${isEmpty ? "Generate" : "Rewrite"} ${traitLabel} for ${characterDisplayName}`,
        cannedResponse: "Ok, let's do it!",
        characterId: character.id,
        traitId,
        traitLabel,
      });
    },
    [globalCtaActive, emitLayoutEvent, character.id, characterDisplayName],
  );

  const applyPortraitCover = useCallback(({ imageId, imageUrl }) => applySavedPortrait(imageId, imageUrl), [applySavedPortrait]);
  const portraitAnalytics = useMemo(
    () => ({
      dialogOpened: (props) => void AppEventPublisher.characterImage.dialogOpened({ characterId: character.id, projectId, ...props }),
      deleted: (props) => void AppEventPublisher.characterImage.deleted({ characterId: character.id, ...props }),
      coverSet: (props) => void AppEventPublisher.characterImage.coverSet({ characterId: character.id, ...props }),
    }),
    [character.id, projectId],
  );
  const removePortrait = useCallback((imageId) => deleteCharacterPortrait(character.id, imageId), [character.id]);
  const setPortraitCover = useCallback((imageId) => setCoverCharacterPortrait(character.id, imageId), [character.id]);
  const portraitCard = useEntityImageCard({
    dialog: portraitDialog,
    entity: character,
    images: portraits,
    removeImage: removePortrait,
    setCoverImage: setPortraitCover,
    applyCover: applyPortraitCover,
    analytics: portraitAnalytics,
    sentryFeaturePrefix: "character_portrait",
    setToast,
  });

  const portraitGeneration = usePortraitGeneration(character.id, {
    onSaved: portraitCard.handleImagesSaved,
    onGenerated: portraits.refetch,
    onCoverSet: applyPortraitCover,
    physicalDescription: physicalDescriptionText,
    pronouns: pronounsText,
  });
  const isPortraitGenerating = portraitGeneration.status !== "idle";

  const restoreCharacter = useCallback(
    async (snapshot) => {
      setIsRestoring(true);
      if (readOnly || !snapshot) return;
      AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.RestoreHistory);
      const response = await Auth.fetchWithIdToken(`/api/characters/${character.id}/restoreSnapshot`, {
        method: "POST",
        snapshotId: snapshot.id,
        projectId,
      });
      updateCharacterData(response.character);
      setCharacter(response.character);
      clearCharacterDiff(character.id);
      setIsRestoring(false);
    },
    [updateCharacterData, clearCharacterDiff, character.id, readOnly, setIsRestoring, projectId, setCharacter],
  );

  const historyController = useElementHistoryController({
    elementId: character.id,
    sourceTable: "characters",
    projectId,
    restore: restoreCharacter,
    listLabel: "Character History",
  });

  // build the view of the selected history item for the history modal
  const selectedHistoryItem = historyController.selectedSnapshot ? (
    <HistorySelectedItem
      key={historyController.selectedSnapshot.id}
      snapshot={historyController.selectedSnapshot}
      projectId={projectId}
    />
  ) : null;

  const handleDelete = async (event) => {
    event.stopPropagation();
    event.preventDefault();
    if (readOnly) return;
    AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.Delete);
    if (isSelected && selectedItems.length > 1) {
      onDeleteMultiple(selectedItems);
    } else {
      onDeleteMultiple([character]);
    }
  };

  const [customFieldIdToFocus, setCustomFieldIdToFocus] = useState(null);
  const cloneTemplateFields = useCallback((fields = []) => fields.map((field) => CustomFieldSchema.cast({ label: field.label || "" })), []);

  const addCustomField = useCallback(async () => {
    AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.AddCustomField);

    let newCustomFieldId = null;
    let overLimit = false;
    setCharacter((draft) => {
      const fields = Array.isArray(draft.customFields) ? draft.customFields : [];
      if (fields.length >= MAX_NUM_CUSTOM_FIELDS) {
        overLimit = true;
        return;
      }
      const newCustomField = CustomFieldSchema.cast({});
      newCustomFieldId = newCustomField.id;
      draft.customFields = [...fields, newCustomField];
    });

    if (overLimit) {
      setToast(`You can only have ${MAX_NUM_CUSTOM_FIELDS} custom fields per character`);
      return;
    }
    if (newCustomFieldId) {
      setCustomFieldIdToFocus(newCustomFieldId);
    }
  }, [setCharacter, setToast]);

  const debouncedValidateForErrors = useMemo(
    () =>
      debounce((char) => {
        CharacterSchema.validate(char)
          .then(() => setError({}))
          .catch((err) => {
            setError(err);
          });
      }, 400),
    [],
  );

  useEffect(() => {
    debouncedValidateForErrors(character);
  }, [character, debouncedValidateForErrors]);

  useEffect(() => () => debouncedValidateForErrors.cancel(), [debouncedValidateForErrors]);

  useEffect(() => {
    if (readOnly || isTogglingCollapsed.current) return;
    if (character !== characterRef.current || isEqual(character, scheduledEntityRef.current)) return;
    scheduledEntityRef.current = character;
    if (!isEqual({ ...character, imageId: null, imageUrl: null }, { ...initialCharacter, imageId: null, imageUrl: null })) {
      setLocalDirty(true);
      updateCharacter(character);
    } else {
      // Converged with the parent copy — nothing unsaved remains, so future parent
      // updates may fully sync again. Cancel any pending debounced save: because this
      // effect also runs on `initialCharacter` changes (before the parent-sync effect's
      // setCharacter commits), response reconciliation can schedule a save of the
      // pre-response character, which would send a redundant stale-version PUT.
      setLocalDirty(false);
      updateCharacter.cancel();
    }
  }, [character, initialCharacter, readOnly, updateCharacter]);

  const propertiesToDisplay = Object.keys(character).filter(
    (key) =>
      ![
        "id", //
        "userId",
        "createdAt",
        "updatedAt",
        "deletedAt",
        "projectId",
      ].includes(key)
  );

  const smallFieldHandlers = useMemo(() => {
    const handlers = {};
    for (const key of SMALL_FIELDS) {
      handlers[key] = {
        onChange: (json) => handleFieldChange(key, json),
        onFocus: () => handleFieldFocus(key),
      };
    }
    return handlers;
  }, [handleFieldChange, handleFieldFocus]);

  const toggleCollapsed = useCallback(async () => {
    // Cancel any pending debounced saves to prevent race condition
    if (typeof updateCharacter.cancel === 'function') {
      updateCharacter.cancel();
    }

    const newCollapsedState = !collapsed;
    const updatedCharacter = { ...character, collapsed: newCollapsedState };
    AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.ToggleCollapse);

    // Track what collapsed state we're expecting from the server
    isTogglingCollapsed.current = true;
    targetCollapsedState.current = newCollapsedState;

    // Optimistically update UI immediately for responsive feel
    setCharacter(updatedCharacter);

    try {
      // saveCharacter calls updateCharacterData which updates parent,
      // parent then updates initialCharacter, which flows back to local state
      // The useEffect will sync when it sees the expected collapsed state
      await saveCharacter(updatedCharacter);
    } catch (err) {
      setToast("Failed to save collapse state");
      // Clear flags on error
      isTogglingCollapsed.current = false;
      targetCollapsedState.current = null;
      // Revert to parent state
      setCharacter(initialCharacter);
    }
  }, [character, collapsed, initialCharacter, saveCharacter, setCharacter, setToast, updateCharacter]);

  useEffect(() => {
    const handleExpandRequest = (event) => {
      const requestId = getStoryBibleEntityExpandRequestId(event);
      if (requestId !== `character::${character.id}` || !character.collapsed || readOnly) {
        return;
      }

      toggleCollapsed();
    };

    window.addEventListener(EXPAND_STORY_BIBLE_ENTITY_EVENT, handleExpandRequest);
    return () => window.removeEventListener(EXPAND_STORY_BIBLE_ENTITY_EVENT, handleExpandRequest);
  }, [character.collapsed, character.id, readOnly, toggleCollapsed]);

  const focusRoleSelectInput = useCallback(
    (selectRefKey) => {
      if (!selectRefKey) return;
      const node = roleSelectRefs.current[selectRefKey];
      if (!node) return;
      focusedElement.current = node;
      focusElement();
    },
    [focusElement, focusedElement]
  );

  const handleRoleSelectChange = useCallback(
    (selectedOption, key = "roles", selectRefKey) => {
      if (!selectedOption) return;
      const roleValue = typeof selectedOption.value === "string" ? selectedOption.value.trim() : "";
      if (!roleValue) return;

      const matchingOption = roleOptionsList.find((option) => option.value === roleValue);
      const isCustomRole = Boolean(selectedOption.__isNew__) || roleValue === "sudo-custom" || !matchingOption || matchingOption?.source === "custom" || matchingOption?.source === "derived";

      if (roleValue === "sudo-custom") {
        focusRoleSelectInput(selectRefKey);
      }

      AppEventPublisher.storyBible.characters.roleChange(roleValue);
      const template = !isCustomRole ? getTemplateForRole?.(roleValue) : null;
      const templateFields = template
        ? cloneTemplateFields(template.defaultFields || [])
        : isCustomRole
          ? cloneTemplateFields(DEFAULT_CHARACTER_CUSTOM_FIELDS)
          : null;

      setCharacter((draft) => {
        draft[key] = roleValue;
        if (!templateFields) {
          return;
        }
        const currentFields = Array.isArray(draft.customFields) ? draft.customFields : [];
        draft.customFields = mergeCustomFieldsWithTemplateOrder(
          currentFields,
          templateFields,
          (tf) => CustomFieldSchema.cast({ label: tf.label || "" }),
        );
      });
    },
    [cloneTemplateFields, focusRoleSelectInput, getTemplateForRole, roleOptionsList, setCharacter]
  );

  const setAiVisibility = (aiVisibility) => {
    AppEventPublisher.storyBible.action(StoryBibleType.characters, StoryBibleAction.ToggleVisibility);
    // Remember the user's choice so the parent-sync effect doesn't revert it to a
    // stale server copy before the autosave lands.
    lastVisibilitySetting.current = aiVisibility;
    const updatedCharacter = { ...character, aiVisibility };
    setCharacter(updatedCharacter);
  };

  const handleCopyCharacter = async () => {
    try {
      const plainText = characterToPlainText(character);
      await navigator.clipboard.writeText(plainText);
      setToast("Character copied");
      AppEventPublisher.storyBible.characters.copyCharacter();
    } catch (error) {
      setToast("Oops, copying failed. Try again");
    }
  };

  const getRoleOption = (role) => {
    if (role === "sudo-custom") {
      return null;
    }
    const preexistingOption = roleOptionsList.find((option) => option.value === role);

    if (preexistingOption) {
      return preexistingOption;
    }

    if (role) {
      return { value: role, label: role };
    }

    return null;
  };

  const actuallyCollapsed = readOnly || collapsed || isDragging;

  const actualDragListeners = readOnly ? {} : dragListeners;
  const dragCursor = readOnly ? "default" : isDragging ? "grabbing" : "grab";
  const motionProps = readOnly
    ? {}
    : {
        //
        initial: { opacity: 0, height: 0 },
        animate: { opacity: 1, height: "auto" },
        exit: { opacity: 0, height: 0 },
        transition: { duration: 0.25, ease: "easeInOut" },
      };

  useEffect(() => {
    if (columnWidths.center.current <= SMALL_WIDTH_BREAKPOINT) {
      setSmallWidth(true);
    } else {
      setSmallWidth(false);
    }

    const collapseIconsBreakpoint = character.seriesId ? COLLAPSE_ICONS_WHEN_SERIES_BREAKPOINT : COLLAPSE_ICONS_BREAKPOINT;

    if (columnWidths.center.current <= collapseIconsBreakpoint) {
      setCollapseIcons(true);
    } else {
      setCollapseIcons(false);
    }

    if (columnWidths.center.current <= MOVE_ROLE_SELECTOR_BREAKPOINT) {
      setMoveRoleSelector(true);
    } else {
      setMoveRoleSelector(false);
    }
  }, [columnWidths.center, character.seriesId]);

  useEffect(() => {
    const fetchCharacterProjectTitle = async () => {
      const characterProject = await Project.get(character.projectId);
      setProjectTitle(characterProject?.title || "");
    };

    fetchCharacterProjectTitle();
  }, [character.projectId]);

  const renderSmallField = (key) => (
    <div
      className="field small-style"
      key={key}
    >
      <div className={cn("key transition-opacity", syncing && "opacity-50")}>{convertKeyToLabel(key)}</div>
      <RestorableTiptapField
        key={getFieldDiffValue(key, character[key]) ? `${key}-diff` : key}
        loading={syncing}
        value={getFieldDiffValue(key, character[key]) ?? character[key]}
        className="tiptap-field"
        onChange={smallFieldHandlers[key].onChange}
        onFocus={smallFieldHandlers[key].onFocus}
        onBlur={onFieldBlur}
        readOnly={readOnly}
        restoring={isRestoring}
        searchableId={`char-${character.id}-${key}`}
        searchableName={`${characterDisplayName}: ${convertKeyToLabel(key)}`}
        userId={userId}
        projectId={projectId}
        ariaLabel={convertKeyToLabel(key)}
        fieldLimitExceeded={oversizedFieldKeys.has(key)}
      />
    </div>
  );

  return (
    <Container
      $moveRoleSelector={moveRoleSelector}
      onMouseEnter={() => setShowHoverIcons(true)}
      onMouseLeave={() => setShowHoverIcons(false)}
      onFocus={() => setShowHoverIcons(true)}
      onBlur={() => setShowHoverIcons(false)}
    >
      <div
        id={`story-bible-character-${character.id}`}
        tabIndex={0}
        className={classNames(
          "form-container",
          "character-card",
          { hidden: isDeleting },
          { "collapsed-entity": actuallyCollapsed },
          { "expanded-entity": !actuallyCollapsed },
          { "selected-entity": isSelected }
        )}
        data-selectable="characters"
        onClick={(e) => {
          handleSelect(character, e.shiftKey, e.ctrlKey || e.metaKey, e);
        }}
      >
        {/* Header Section */}
        <div className={classNames("header", { "small-width": smallWidth || moveRoleSelector }, { "read-only": readOnly })}>
          <div className={`field title-display dropdown left-side-container`}>
            {!readOnly && (
              <motion.div
                ref={setActivatorNodeRef}
                animate={{
                  cursor: draggingLock ? "not-allowed" : dragCursor,
                  pointerEvents: draggingLock ? "none" : "auto",
                  opacity: draggingLock ? 0.4 : 1,
                  scale: draggingLock ? 0.8 : 1,
                }}
                className="grabber"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                {...actualDragListeners}
              >
                <div className="grabber-container">
                  <InlineSVG
                    style={{ opacity: showHoverIcons ? 1 : 0 }}
                    src="/assets/grabber.svg"
                  />
                </div>
              </motion.div>
            )}
            <div className="character-header-left-side">
              {!readOnly && (
                <div
                  className="chevron-container"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      if (readOnly) return;
                      toggleCollapsed();
                    }
                  }}
                >
                  <SVG
                    src="/assets/chevron-down.svg"
                    className={classNames("chevron", { "rotated-chevron": actuallyCollapsed })}
                    alt="disclosure arrow"
                    onClick={() => {
                      if (readOnly) return;
                      toggleCollapsed();
                    }}
                    aria-label={`${actuallyCollapsed ? "Expand" : "Collapse"} character details`}
                  />
                </div>
              )}
              <EntityImageRowThumbnail
                visible={collapsed || isDragging}
                imageUrl={character.imageUrl}
                imageFailed={portraitCard.coverImageFailed}
                onImageError={portraitCard.markCoverImageFailed}
                isGenerating={isPortraitGenerating}
                readOnly={readOnly}
                onClick={portraitCard.handleCoverClick}
                ariaLabel={`${characterDisplayName}'s portrait${isPortraitGenerating ? " (generating)" : ""}`}
                tooltipText={readOnly ? "No portrait yet" : "Add a portrait"}
                tooltipDisabled={!!character.imageUrl && !portraitCard.coverImageFailed}
              />
              <div className="name-and-role">
                <RestorableTiptapField
                  key={getFieldDiffValue("name", character.name) ? "name-diff" : "name"}
                  loading={syncing}
                  value={getFieldDiffValue("name", character.name) ?? character.name}
                  className="character-name"
                  placeholder="Name"
                  onChange={handleNameChange}
                  onFocus={handleNameFocus}
                  onBlur={onFieldBlur}
                  readOnly={readOnly}
                  restoring={isRestoring}
                  searchableId={`char-${character.id}-name`}
                  searchableName={`Character: ${characterNameText || "Name"}`}
                  userId={userId}
                  projectId={projectId}
                  ariaLabel="Character name"
                  fieldLimitExceeded={oversizedFieldKeys.has("name")}
                />

                <StyledSelect
                  disabled={syncing}
                  className={cn("role-selector transition-opacity", syncing && "opacity-50")}
                  styleOverrides={{
                    backgroundColor: themeVariables.documentBackground,
                    ...(oversizedFieldKeys.has("roles")
                      ? { boxShadow: "inset 0 0 0 1px var(--error-red, #d14343)", borderRadius: "6px" }
                      : {}),
                  }}
                  placeholder="Type"
                  creatable={true}
                  value={getRoleOption(character.roles)}
                  options={roleOptionsList}
                  onChange={(selectedOption) => handleRoleSelectChange(selectedOption, "roles", simplifiedRoleSelectRefKey)}
                  onMenuOpen={() => setIsFlat(true)}
                  onMenuClose={() => setIsFlat(false)}
                  isDisabled={readOnly || syncing}
                  aria-label="Select character role"
                  readOnly={readOnly}
                  ref={(node) => setRoleSelectRef(simplifiedRoleSelectRefKey, node)}
                />
                {character.seriesId && <SeriesTag projectTitle={projectTitle} />}
              </div>
            </div>
          </div>
          {!readOnly && !collapseIcons && (
            <div className="character-header-right-side">
              <ElementVisibilityController
                onClick={() => {
                  setAiVisibility(character.aiVisibility === AI_VISIBILITY.HIDDEN ? AI_VISIBILITY.AUTO : AI_VISIBILITY.HIDDEN);
                }}
                isVisible={character.aiVisibility !== AI_VISIBILITY.HIDDEN}
                shouldShow={showHoverIcons}
                faded={false}
              />
              <AnimatedWrapper isVisible={showHoverIcons}>
                <IconButton
                  className="history"
                  onClick={() => historyController.open()}
                  src="/assets/clock.svg"
                  ariaLabel="Open History"
                  hoverTipText="History"
                  iconSize={20}
                />
              </AnimatedWrapper>
              <AnimatedWrapper isVisible={showHoverIcons}>
                <IconButton
                  onClick={handleCopyCharacter}
                  src="/assets/duplicate.svg"
                  ariaLabel="Copy Character"
                  hoverTipText="Copy"
                  iconSize={20}
                />
              </AnimatedWrapper>
              <DotsDeleteMenu
                handleDelete={handleDelete}
                disabled={readOnly}
                setIsFlat={setIsFlat}
                selectedItems={selectedItems}
                isSelected={isSelected}
              />
            </div>
          )}
          {!readOnly && collapseIcons && (
            <DotsDeleteMenu
              handleDelete={handleDelete}
              disabled={readOnly}
              setIsFlat={setIsFlat}
              selectedItems={selectedItems}
              isSelected={isSelected}
              otherActions={[
                <ElementVisibilityController
                  key="visibility"
                  onClick={() => setAiVisibility(character.aiVisibility === AI_VISIBILITY.HIDDEN ? AI_VISIBILITY.AUTO : AI_VISIBILITY.HIDDEN)}
                  isVisible={character.aiVisibility !== AI_VISIBILITY.HIDDEN}
                  shouldShow={true}
                  faded={false}
                  isMenuButton={true}
                />,
                <div
                  key="history"
                  className="menu-item"
                  onClick={() => historyController.open()}
                >
                  <div className="icon">
                    <SVG src="/assets/clock.svg" />
                  </div>
                  <div className="label">History</div>
                </div>,
                <div
                  key="copy"
                  className="menu-item"
                  onClick={handleCopyCharacter}
                >
                  <div className="icon">
                    <SVG src="/assets/duplicate.svg" />
                  </div>
                  <div className="label">Copy</div>
                </div>,
              ]}
            />
          )}
        </div>

        {/* We do not animate while dragging */}
        {!isDragging && (
          <AnimatePresence>
            {!collapsed && (
              <motion.div {...motionProps}>
                <div className="flex flex-col gap-[16px]">
                  <div className="field portrait-field flex flex-col gap-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className={cn("key transition-opacity", syncing && "opacity-50")}>Portraits</div>
                      {/* The preview grid swaps its inline "+" add tile for a "+N" overflow tile
                          once alternates exceed the visible cap, leaving no add affordance on the
                          card — this header button covers only that case. */}
                      {!readOnly && portraits.totalAlternateCount > MAX_VISIBLE_ALTERNATES && (
                        <SudoButton
                          variant="tertiary"
                          size="small"
                          leftIcon="/assets/add.svg"
                          onClick={portraitCard.handleAddClick}
                          disabled={syncing}
                        >
                          Add portrait
                        </SudoButton>
                      )}
                    </div>
                    <EntityImageGalleryPreview
                      images={portraits.images}
                      totalAlternateCount={portraits.totalAlternateCount}
                      isLoading={portraits.isLoading}
                      coverImageUrl={character.imageUrl}
                      coverImageFailed={portraitCard.coverImageFailed}
                      onCoverImageError={portraitCard.markCoverImageFailed}
                      isGenerating={isPortraitGenerating}
                      readOnly={readOnly}
                      onCoverClick={portraitCard.handleCoverClick}
                      onAddClick={portraitCard.handleAddClick}
                      onAlternateClick={portraitCard.handleAlternateClick}
                      onOverflowClick={portraitCard.handleOverflowClick}
                      onRemoveImage={portraitCard.handleDeleteImage}
                      entityDisplayName={characterDisplayName}
                    />
                  </div>
                  {/* Small Field Row Section */}
                  <div className="small-field-row">{propertiesToDisplay.filter((key) => SMALL_FIELDS.includes(key)).map(renderSmallField)}</div>
                  {/* Rest of the field use large editors */}
                  <div className="tiptap-fields flex flex-col gap-[16px]">
                    {character &&
                      character.customFields?.length > 0 &&
                      character.customFields.map((field, index) => {
                        const customFieldKey = `custom.${field.label}`;
                        const customFieldDiffValue = getFieldDiffValue(customFieldKey, field.value);
                        return (
                          <CustomField
                            loading={syncing}
                            key={field.id || index}
                            field={field}
                            index={index}
                            setEntity={setCharacter}
                            readOnly={readOnly}
                            isRestoring={isRestoring}
                            isFlat={isFlat}
                            setIsFlat={setIsFlat}
                            regenerateTrait={regenerateTrait}
                            autoFocus={field.id === customFieldIdToFocus}
                            searchableId={`char-${character.id}-${field.id}`}
                            searchableName={`${characterDisplayName}: ${field.label}`}
                            onBlur={onFieldBlur}
                            userId={userId}
                            projectId={projectId}
                            diffValue={customFieldDiffValue}
                            diffFieldKey={customFieldKey}
                            onDiffFieldChange={clearFieldHighlighting}
                            onClearDiff={clearFieldHighlighting}
                            characterName={characterDisplayName}
                            ctaEnabled={!!hasAccessToAgenticChat}
                            ctaShimmer={!!traitSignals[getTraitCTASignal(character.id, field.id)]}
                            globalCtaActive={!!globalCtaActive}
                            onCtaTrigger={handleTraitCtaTrigger}
                            fieldLimitExceeded={oversizedFieldKeys.has(`customField:${field.id}`)}
                          />
                        );
                      })}
                  </div>
                  <div className="character-footer">
                    {readOnly ? null : (
                      <SudoButton
                        variant="tertiary"
                        onClick={(e) => addCustomField()}
                        leftIcon="/assets/add.svg"
                        disabled={readOnly || syncing}
                        size="small"
                      >
                        Add Trait
                      </SudoButton>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
      {error.message && <div className="error">Cannot save character: {error.message}</div>}

      {/* Placing the modal here instead of using the ElementHistory component
      allows us to collapse the history icon into the ... menu and still use the modal */}
      <HistoryModal
        isOpen={historyController.modalProps.isOpen}
        onClose={historyController.modalProps.onClose}
        selectedItem={selectedHistoryItem}
        historyItems={[]}
        onSelectItem={historyController.setSelectedSnapshot}
        onRestoreItem={historyController.modalProps.onRestoreItem}
        titleIcon="/assets/clock.svg"
        title={"Character History"}
        selectedItemId={historyController.selectedSnapshot?.id}
        sourceTable={"characters"}
        loading={historyController.modalProps.loading}
        restoring={historyController.modalProps.restoring}
        fetchHistory={historyController.fetchHistory}
        usePagination={true}
        isCreatingSnapshot={historyController.modalProps.isCreatingSnapshot}
      />
      <CharacterPortraitDialog
        {...portraitCard.dialogProps}
        characterId={character.id}
        characterName={characterDisplayName}
        characterRoleLabel={character.roles ? formatCharacterRoleLabel(character.roles) : null}
        characterImageUrl={portraitCard.coverImageFailed ? null : character.imageUrl}
        characterImagePrompt={portraitCard.coverImagePrompt}
        portraitsLoading={portraits.isLoading}
        onAddPortrait={portraitCard.handleGenerateFromGallery}
        {...portraitGeneration}
      />
    </Container>
  );
}

export const DotsDeleteMenu = ({ handleDelete, disabled, setIsFlat, selectedItems, isSelected, otherActions }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  const handleMenuClick = useCallback(() => {
    setVisible((current) => !current);
    setIsFlat(true);
  }, [setIsFlat]);

  useOnClickOutside(ref, () => {
    setVisible(false);
    setIsFlat(false);
  });

  return (
    <DotsDeleteMenuContainer
      className="item-menu"
      aria-label={`${visible ? "close" : "open"} actions menu`}
    >
      <a className="hotdog-dots">
        <IconButton
          onClick={handleMenuClick}
          src="/assets/three-dots.svg"
          disabled={disabled}
          ariaLabel="character actions menu"
        />
      </a>

      {visible && (
        <div
          className={classNames("custom-field-action-menu", { visible })}
          ref={ref}
        >
          <div className={classNames("custom-field-action-menu__inner")}>
            <div className="actions">
              {/* When width is narrow we collapse other icons into this menu */}
              {!!otherActions && otherActions.map((action) => action)}
              <div
                onClick={handleDelete}
                key="delete"
                className={classNames("menu-item", "delete")}
              >
                <div className="icon">
                  {" "}
                  <SVG
                    src="/assets/delete.svg"
                    aria-hidden="true"
                  />
                </div>
                <div className="label">{isSelected && selectedItems.length > 1 ? `Delete ${selectedItems.length} characters` : "Delete"}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </DotsDeleteMenuContainer>
  );
};
